import React, { useState, useEffect } from 'react';
import { Send, Users, Calendar, Mail, Target } from 'lucide-react';
import FormModal from './FormModal';

interface EmailCampaign {
  id: number;
  name: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled';
  template_id: number;
  trigger_id?: number;
  recipient_config: any;
  schedule_config?: any;
  content_config?: any;
  recipient_count: number;
  sent_count: number;
  created_at: string;
  updated_at: string;
}

interface EmailTemplate {
  id: number;
  name: string;
  category: string;
}

interface EmailTrigger {
  id: number;
  name: string;
  type: string;
}

interface EmailCampaignModalProps {
  onClose: () => void;
  onSubmit: (campaign: Partial<EmailCampaign>) => Promise<void> | void;
  selectedCampaign: EmailCampaign | null;
  templates: EmailTemplate[];
  triggers: EmailTrigger[];
}

export const EmailCampaignModal: React.FC<EmailCampaignModalProps> = ({
  onClose,
  onSubmit,
  selectedCampaign,
  templates,
  triggers,
}) => {
  const [campaign, setCampaign] = useState<Partial<EmailCampaign>>(
    selectedCampaign || {
      name: '',
      subject: '',
      status: 'draft',
      template_id: 0,
      recipient_config: {
        type: 'all_users',
        filters: {}
      },
      schedule_config: {
        type: 'immediate'
      }
    }
  );

  const [loading, _setLoading] = useState(false);

  // Update state when selectedCampaign changes
  useEffect(() => {
    if (selectedCampaign) {
      setCampaign(selectedCampaign);
    }
  }, [selectedCampaign]);
  const [activeTab, setActiveTab] = useState<'basic' | 'recipients' | 'schedule' | 'content'>('basic');

  // Update state when selectedCampaign changes
  useEffect(() => {
    if (selectedCampaign) {
      setCampaign(selectedCampaign);
    }
  }, [selectedCampaign]);

  const handleSubmit = async () => {
    try {
      await onSubmit(campaign);
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedCampaign?.id;

  const recipientTypes = [
    { value: 'all_users', label: 'All Users', description: 'Send to all registered users' },
    { value: 'role_based', label: 'By Role', description: 'Target specific user roles' },
    { value: 'company_based', label: 'By Company', description: 'Target users from specific companies' },
    { value: 'segment', label: 'Custom Segment', description: 'Use saved user segments' },
    { value: 'manual', label: 'Manual List', description: 'Upload or select specific users' }
  ];

  const scheduleTypes = [
    { value: 'immediate', label: 'Send Immediately', description: 'Send as soon as campaign is launched' },
    { value: 'scheduled', label: 'Schedule for Later', description: 'Choose specific date and time' },
    { value: 'trigger', label: 'Trigger-Based', description: 'Send when trigger conditions are met' },
    { value: 'recurring', label: 'Recurring Schedule', description: 'Send on a regular schedule' }
  ];

  return (
    <FormModal
      title={isCreate ? 'Create Email Campaign' : 'Edit Email Campaign'}
      subtitle={isCreate ? 'Set up a new email campaign' : 'Modify campaign settings'}
      icon={<Send className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Campaign' : 'Update Campaign'}
      cancelText="Cancel"
      loading={loading}
      size="large"
    >
      {/* Tab Navigation */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'basic', label: 'Basic Info', icon: Mail },
            { id: 'recipients', label: 'Recipients', icon: Users },
            { id: 'schedule', label: 'Schedule', icon: Calendar },
            { id: 'content', label: 'Content', icon: Target }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-nz-blue text-nz-blue'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-6">
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Campaign Name *</label>
                <input
                  type="text"
                  value={campaign.name || ''}
                  onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                  placeholder="e.g., Monthly Compliance Update"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Email Subject *</label>
                <input
                  type="text"
                  value={campaign.subject || ''}
                  onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                  placeholder="Subject line for the email"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Email Template</label>
              <select
                value={campaign.template_id || ''}
                onChange={(e) => setCampaign({ ...campaign, template_id: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={loading}
              >
                <option value="">Select a template</option>
                {Array.isArray(templates) && templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.category})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Campaign Status</label>
              <select
                value={campaign.status || 'draft'}
                onChange={(e) => setCampaign({ ...campaign, status: e.target.value as any })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={loading}
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'recipients' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Recipient Type</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recipientTypes.map(type => (
                  <div
                    key={type.value}
                    onClick={() => setCampaign({
                      ...campaign,
                      recipient_config: { ...campaign.recipient_config, type: type.value }
                    })}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      campaign.recipient_config?.type === type.value
                        ? 'border-nz-blue bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <h4 className="font-medium text-slate-900 mb-1">{type.label}</h4>
                    <p className="text-sm text-slate-600">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recipient Configuration based on type */}
            {campaign.recipient_config?.type === 'role_based' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Role Selection</h3>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Select Roles</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {['admin', 'auditor', 'company_admin', 'user'].map(role => (
                      <label key={role} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={campaign.recipient_config?.filters?.roles?.includes(role) || false}
                          onChange={(e) => {
                            const roles = campaign.recipient_config?.filters?.roles || [];
                            const newRoles = e.target.checked
                              ? [...roles, role]
                              : roles.filter((r: string) => r !== role);
                            setCampaign({
                              ...campaign,
                              recipient_config: {
                                ...campaign.recipient_config,
                                filters: { ...campaign.recipient_config?.filters, roles: newRoles }
                              }
                            });
                          }}
                          className="h-4 w-4 text-nz-blue rounded focus:ring-nz-blue"
                        />
                        <span className="ml-2 text-sm text-slate-700 capitalize">
                          {role.replace('_', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {campaign.recipient_config?.type === 'company_based' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Company Selection</h3>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Select Companies</label>
                  <input
                    type="text"
                    placeholder="Enter company IDs or names (comma-separated)"
                    value={campaign.recipient_config?.filters?.companies?.join(', ') || ''}
                    onChange={(e) => setCampaign({
                      ...campaign,
                      recipient_config: {
                        ...campaign.recipient_config,
                        filters: {
                          ...campaign.recipient_config?.filters,
                          companies: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        }
                      }
                    })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Schedule Type</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {scheduleTypes.map(type => (
                  <div
                    key={type.value}
                    onClick={() => setCampaign({
                      ...campaign,
                      schedule_config: { ...campaign.schedule_config, type: type.value }
                    })}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      campaign.schedule_config?.type === type.value
                        ? 'border-nz-blue bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <h4 className="font-medium text-slate-900 mb-1">{type.label}</h4>
                    <p className="text-sm text-slate-600">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Schedule Configuration */}
            {campaign.schedule_config?.type === 'scheduled' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Schedule Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Send Date</label>
                    <input
                      type="date"
                      value={campaign.schedule_config?.send_date || ''}
                      onChange={(e) => setCampaign({
                        ...campaign,
                        schedule_config: { ...campaign.schedule_config, send_date: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Send Time</label>
                    <input
                      type="time"
                      value={campaign.schedule_config?.send_time || '09:00'}
                      onChange={(e) => setCampaign({
                        ...campaign,
                        schedule_config: { ...campaign.schedule_config, send_time: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}

            {campaign.schedule_config?.type === 'trigger' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Trigger Selection</h3>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Select Trigger</label>
                  <select
                    value={campaign.trigger_id || ''}
                    onChange={(e) => setCampaign({ ...campaign, trigger_id: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                    disabled={loading}
                  >
                    <option value="">Select a trigger</option>
                    {Array.isArray(triggers) && triggers.map(trigger => (
                      <option key={trigger.id} value={trigger.id}>
                        {trigger.name} ({trigger.type})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {campaign.schedule_config?.type === 'recurring' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Recurring Schedule</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Frequency</label>
                    <select
                      value={campaign.schedule_config?.frequency || 'weekly'}
                      onChange={(e) => setCampaign({
                        ...campaign,
                        schedule_config: { ...campaign.schedule_config, frequency: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                      disabled={loading}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Send Time</label>
                    <input
                      type="time"
                      value={campaign.schedule_config?.send_time || '09:00'}
                      onChange={(e) => setCampaign({
                        ...campaign,
                        schedule_config: { ...campaign.schedule_config, send_time: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">End Date (Optional)</label>
                    <input
                      type="date"
                      value={campaign.schedule_config?.end_date || ''}
                      onChange={(e) => setCampaign({
                        ...campaign,
                        schedule_config: { ...campaign.schedule_config, end_date: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Content Preview</h3>
              {campaign.template_id ? (
                <div className="space-y-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Subject Line</h4>
                    <p className="text-slate-700">{campaign.subject || 'No subject set'}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Template</h4>
                    <p className="text-slate-600">
                      {templates.find(t => t.id === campaign.template_id)?.name || 'No template selected'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mail className="mx-auto text-slate-400 mb-2" size={48} />
                  <p className="text-slate-600">Select a template to preview content</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">A/B Testing</label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={campaign.content_config?.ab_testing || false}
                  onChange={(e) => setCampaign({
                    ...campaign,
                    content_config: { ...campaign.content_config, ab_testing: e.target.checked }
                  })}
                  className="h-5 w-5 text-nz-blue rounded focus:ring-nz-blue"
                  disabled={loading}
                />
                <span className="ml-3 text-sm text-slate-700">Enable A/B testing for this campaign</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};